import { useEffect, useState } from 'react';

/**
 * True once something that is `active` has waited longer than `afterMs`, and
 * it stays true until `resolved`.
 *
 * For a wait that can fail without ever reporting an error: the current-user
 * query retries, errors, and starts over, so its status flips between loading
 * and not while nothing ever arrives, and a screen gated on it stays blank. A
 * brief break in `active` must not clear the verdict, or the way out flickers
 * back to blank; only the thing arriving does. `resolved` defaults to "not
 * active" for a wait that simply ends.
 */
export function useStalled(active: boolean, afterMs: number, resolved: boolean = !active): boolean {
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    if (!active) {
      return;
    }

    const timeoutId = setTimeout(() => setStalled(true), afterMs);

    return () => clearTimeout(timeoutId);
  }, [active, afterMs]);

  useEffect(() => {
    if (resolved) {
      setStalled(false);
    }
  }, [resolved]);

  return stalled && !resolved;
}
